import { Component, OnInit, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../../../service/auth/auth.service';
import { ModalLoadingComponent } from '../../../../../shared/components/loads/modal-loading/modal-loading.component';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { HttpService } from 'src/app/service/http/http.service';
import { TextInput } from 'src/utils/models/input.interface';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { PasswordInputComponent } from 'src/app/shared/components/inputs/password-input/password-input.component';
import { TextInputComponent } from 'src/app/shared/components/inputs/text-input/text-input.component';
import { ButtonComponent } from 'src/app/shared/components/inputs/button/button.component';
import { CarouselComponent, CarouselSlide } from 'src/app/shared/components/common/carousel/carousel.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    SharedModule,
    PasswordInputComponent,
    TextInputComponent,
    ButtonComponent,
    CarouselComponent
  ],
})
export class LoginComponent implements OnInit {
  textInterface: TextInput;
  passwordInterface: TextInput;
  form: FormGroup

  isBasicLogin = signal(true);
  isSAMLLogin = signal(false);
  isLoading = signal(false);
  loginError = signal<any>(null);
  projectName: string = 'Gemminus Data Layer';

  showLoginForm = false;
  carouselSlides: CarouselSlide[] = [];

  constructor(
    private readonly auth: AuthService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    public readonly dialog: MatDialog,
    private readonly msalService: MsalService,
    private readonly httpService: HttpService,
    private fb: FormBuilder
  ) {
    this.msalService.handleRedirectObservable().subscribe();
  }

  ngOnInit(): void {
    this.initializeLoginComponents();
  }

  private initializeLoginComponents(): void {
    this.auth.removeLocalStorage();
    this.form = this.fb.group({
      email: new FormControl<string>('', [Validators.required]),
      password: new FormControl<string>('', [Validators.required])
    });
    this.setInterfaces();
    this.initializeCarouselSlides();
  }

  setInterfaces(): void {
    this.textInterface = { value: this.form.value.email, label: 'Email', maxLength: '60', placeholder: 'joao@gmail.com' };
    this.passwordInterface = { value: this.form.value.password, label: 'Password', maxLength: '60', placeholder: '123' };
  }

  initializeCarouselSlides(): void {
    this.carouselSlides = [
      {
        title: `${this.projectName} plataforma unificada`,
        description: 'Reúna os produtores e consumidores de dados em uma única plataforma e acelere a produtividade. A colaboração melhora com pessoas e dados de Múltiplas Plataformas de Dados em um local centralizado.',
        imageSrc: '../../../../../../assets/images/data-observability.png',
        imageAlt: 'Plataforma Collate'
      },
      {
        title: 'Dashboard Intuitivo',
        description: 'Visualize e analise seus dados de forma clara e eficiente com nosso dashboard moderno e intuitivo.',
        imageSrc: '../../../../../../assets/images/dashboard.png',
        imageAlt: 'Dashboard'
      },
      {
        title: 'Análise de Dados Avançada',
        description: 'Descubra insights valiosos através de análises avançadas e relatórios detalhados dos seus dados.',
        imageSrc: '../../../../../../assets/images/temporal.png',
        imageAlt: 'Analytics'
      }
    ];
  }

  handleLoginBasic(): void {
    this.isLoading.set(true);
    this.loginError.set(null);

    const loadingDialog = this.openLoadingDialog();
    this.httpService.genericPost('auth/basic', this.form.value, '').subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.auth.saveLocalStorage(res).then((validation) => {
          if (validation) {
            this.loadAndSaveProjectId(loadingDialog);
          }
        });
      },
      error: (err) => {
        this.loginError.set(err);
        this.isLoading.set(false);
        loadingDialog.close();

        const errorMessage = err?.error?.message || err?.message;
        const translateMessage = this.translateMessage(errorMessage);
        this.toastr.show(translateMessage, 'Erro', {
          timeOut: 6000,
          messageClass: 'error'
        });
      }
    });
  }

  handleLoginSAML() {
    this.httpService.genericGet('auth/saml').subscribe(
      {
        next: (res) => { },
        error: (err) => {
          window.location.href = err.url;
        }
      }
    )
  }

  handleLogin() {
    const loadingDialog = this.openLoadingDialog();

    this.msalService.loginPopup().subscribe({
      next: (response: AuthenticationResult) => {
        const data = {
          access_token: response.accessToken
        }
        this.httpService.genericPost('auth/azureAd', data, '').subscribe({
          next: (user) => {
            this.auth.saveLocalStorage(user).then((validation) => {
              if (validation) {
                this.loadAndSaveProjectId(loadingDialog);
              }
            });
          },
          error: (err) => {
            loadingDialog.close()
            this.toastr.show('Algo de errado aconteceu, tente novamente mais tarde.', 'Error', { timeOut: 6000, messageClass: 'error' });
          }
        })
      },
      error: () => {
        loadingDialog.close()
        this.toastr.show('Algo de errado aconteceu, tente novamente mais tarde.', 'Error', { timeOut: 6000, messageClass: 'error' });
      }
    })
  }

  private openLoadingDialog(): MatDialogRef<ModalLoadingComponent> {
    return this.dialog.open(ModalLoadingComponent, {
      width: '500px',
      height: '348px',
      disableClose: false,
      data: {
        title: 'Carregando',
        text: 'Por favor espere alguns segundos'
      }
    });
  }

  private loadAndSaveProjectId(loadingDialog: MatDialogRef<ModalLoadingComponent>, _route?: string): void {
    this.httpService.genericGet<any[]>('projects').subscribe({
      next: (projects: any) => {
        if (projects && projects.count > 0) {
          const projectId = projects.rows[0].id || projects.rows[0]._id;
          sessionStorage.setItem('projectId', projectId);
        }
        loadingDialog.close();
        this.toastr.show('Autenticado com sucesso', 'Sucesso',
          { timeOut: 2000, messageClass: 'success' }
        );
        this.router.navigate(['/conexoes']);
      },
      error: (err) => {
        console.error('Erro ao carregar projetos:', err);
        loadingDialog.close();
        this.toastr.show('Autenticado com sucesso', 'Sucesso',
          { timeOut: 2000, messageClass: 'success' }
        );
        this.router.navigate(['/conexoes']);
      }
    });
  }

  private translateMessage(message: string): string {
    if (message.includes('Invalid')) return 'Usuário ou senha incorretos. Tente novamente.';
    if (message.includes('not found')) return 'Usuário não encontrado. Tente novamente.';
    if (message.includes('expired')) return 'Sessão expirada. Faça login novamente.';
    if (message.includes('unauthorized')) return 'Acesso não autorizado. Tente novamente.';
    if (message.includes('forbidden')) return 'Acesso proibido. Tente novamente.';
    if (message.includes('bad request')) return 'Requisição inválida. Tente novamente.';
    if (message.includes('internal server error')) return 'Erro interno do servidor. Tente novamente mais tarde.';
    if (message.includes('service unavailable')) return 'Serviço indisponível. Tente novamente mais tarde.';
    return message;
  }

  flipToLogin(): void {
    this.showLoginForm = true;
  }

  flipToWelcome(): void {
    this.showLoginForm = false;
  }
}
